<template>
  <div class="email-token-unified-form">
    <div class="intro-section__headline">
      <h4 class="intro-section__headline__txt" v-if="step==='email-input'">{{
          $t('forms.email_token_login.sign_in')
        }}</h4>
      <div v-else>
        <Icon name="grMail05" width="26"/>
        <h4 class="intro-section__headline__txt big">{{ $t('forms.email_token_login.verify_title') }}</h4>
      </div>
    </div>

    <!-- Step 1: Email Input -->
    <div v-if="step === 'email-input'" class="step-email intro-section__form">
      <div class="form-alert-errors" v-if="serverErrors && serverErrors.length > 0">
        <ul class="list-unstyled m-0">
          <li v-for="(error, i) in serverErrors" :key="i">{{ error }}</li>
        </ul>
      </div>

      <form @submit.prevent="handleEmailSubmit">
        <b-form-group :label="$t('forms.email_token_login.email_placeholder')">
          <b-input-group>
            <b-input-group-prepend is-text :class="{'disabled': showPrivacyCheckboxes}">
              <Icon name="mail01" width="20"/>
            </b-input-group-prepend>
            <b-form-input
                id="email"
                type="email"
                required
                class="account-step__email"
                v-model="email"
                :placeholder="$t('forms.email_token_login.email_placeholder')"
                :disabled="showPrivacyCheckboxes || isEmailReadonly"
                :readonly="isEmailReadonly"
            />
            <b-input-group-append is-text v-if="showPrivacyCheckboxes && !isEmailReadonly" class="cursor-pointer" @click="goBackToEmail">
                <Icon name="pencil" width="20"/>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <!-- Privacy checkboxes (shown for new users after email check) -->
        <div v-if="showPrivacyCheckboxes" class="form-check-container my-2">
          <div class="form-check" v-if="getAppConfigReady">
            <div v-if="$store.state.appConfig.privacyCheckboxes && getPrivacyCheckboxes.length">
              <template v-for="checkbox in getPrivacyCheckboxes">
                <b-form-checkbox
                    v-model="userCheckboxChoices[checkbox.field_name]"
                    :required="checkbox.is_required"
                    :name="checkbox.field_name"
                    :value="true"
                    :key="checkbox.field_name"
                    :unchecked-value="false"
                    :disabled="sending"
                >
                  <span v-html="checkbox.label"></span>
                </b-form-checkbox>
              </template>
            </div>
            <div v-else>
              <b-form-checkbox
                  v-model="consent_privacy_and_terms"
                  required
                  name="consent_privacy_and_terms"
                  value="true"
                  unchecked-value="false"
                  :disabled="sending"
              >
                <span v-html="privacyStatement"></span>
              </b-form-checkbox>
            </div>

            <span class="form-group--error__message" v-if="hasValidationError('consent_privacy_and_terms')">
              {{ validationErrors.consent_privacy_and_terms }}
            </span>
          </div>
        </div>

        <div class="form-group m-0">
          <button
              type="submit"
              class="btn btn-primary m-0 w-100"
              :disabled="sending"
          >
            <span v-if="!sending">{{
                showPrivacyCheckboxes ? $t('forms.email_token_login.send_code') : $t('forms.email_token_login.continue_btn')
              }}</span>
            <span v-else>{{ $t('forms.email_token_login.checking') }}</span>
          </button>

          <div class="account-step__info mt-3">
            <div class="in d-flex align-items-center">
              <div class="mr-2">
                <Icon name="star06" width="24"/>
              </div>
              <div class="text-left small">
                {{ $t('forms.email_token_login.instructions') }}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Step 2: Token Verification -->
    <div v-if="step === 'token-verification'" class="text-center">
      <p class="text-secondary token-info" v-html="$t('forms.email_token_login.verify_description', {email})"></p>

      <TokenInput
          ref="tokenInput"
          :error-message="verifyError"
          @complete="verifyToken"
          @update="clearError"
      />

      <div class="text-center mt-1">
        <!-- Resend - always visible -->
        <p class="text-secondary small my-3" v-if="canResend && !resending">
          {{ $t('forms.email_token_login.havent_received') }}
          <a href="#" style="text-decoration: underline" @click.prevent="resendToken">{{ $t('forms.email_token_login.resend_code') }}</a>
        </p>
        <p class="text-secondary small my-3" v-else>
          {{$t('forms.email_token_login.resend_wait', {seconds: resendCountdown})}}
        </p>
        <!-- Change email (hidden when email is locked from external source) -->
        <div class="mt-4" v-if="!isEmailLocked">
          <button
              @click="goBackToEmail"
              class="btn btn-link btn-sm"
          >
            <u>{{ $t('forms.email_token_login.change_email') }}</u>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import TokenInput from '../TokenInput.vue'

export default {
  name: 'EmailTokenUnifiedForm',

  components: {
    TokenInput
  },

  data() {
    return {
      step: 'email-input', // 'email-input' | 'token-verification'
      email: '',
      isNewUser: false,
      showPrivacyCheckboxes: false,
      consent_privacy_and_terms: false,
      userCheckboxChoices: {},
      validationErrors: {},
      sending: false,
      resending: false,
      serverErrors: [],
      verifyError: null,
      resendCountdown: 0,
      resendTimer: null
    }
  },

  computed: {
    ...mapGetters(['thirdLevelDomain']),
    ...mapGetters('appConfig', ['getPrivacyCheckboxes', 'getAppConfigReady', 'getEventOwnerId']),
    ...mapGetters('auth', ['prefilledEmail']),

    /**
     * Whether email is locked from external source (preview JWT, etc.)
     * When locked, user cannot modify or change the email
     */
    isEmailLocked() {
      return !!this.prefilledEmail
    },

    /**
     * Whether email field should be readonly (when pre-filled from external source)
     */
    isEmailReadonly() {
      return this.isEmailLocked && this.step === 'email-input'
    },

    canResend() {
      return this.resendCountdown === 0
    },

    privacyStatement() {
      return this.$t('forms.signup_form.privacy_statement', {
        event_owner: this.thirdLevelDomain,
        policy_url: this.policyUrl,
        terms_url: this.termsUrl
      })
    },

    policyUrl() {
      return `https://policy.getpica.com/${this.thirdLevelDomain}/privacy`
    },

    termsUrl() {
      return `https://policy.getpica.com/${this.thirdLevelDomain}/terms`
    }
  },

  mounted() {
    // Pre-fill email if available from external source (e.g., preview API)
    if (this.prefilledEmail) {
      this.email = this.prefilledEmail
    }
  },

  beforeDestroy() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
    }

    // Clear prefilled email when leaving component
    if (this.prefilledEmail) {
      this.$store.commit('auth/CLEAR_CONTEXT_PARAM', 'email')
    }
  },

  watch: {
    step(newStep) {
      // Emit step change so parent can hide/show elements
      this.$emit('step-change', newStep)
    },

    getAppConfigReady: {
      handler() {
        this.setUserCheckboxChoices()
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('auth', ['requestLogin', 'registerUser', 'authenticate']),

    setUserCheckboxChoices() {
      if (this.getPrivacyCheckboxes && this.getPrivacyCheckboxes.length) {
        const choices = {}
        this.getPrivacyCheckboxes.forEach(checkbox => {
          choices[checkbox.field_name] = false
        })
        this.userCheckboxChoices = choices
      }
    },

    hasValidationError(field) {
      return this.validationErrors && this.validationErrors[field]
    },

    async handleEmailSubmit() {
      this.serverErrors = []
      this.validationErrors = {}

      // hide site banner if visible
      document.body.classList.add('site-banner--hidden')
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 400)

      // If this is a new user and privacy checkboxes are shown, register the user
      if (this.isNewUser && this.showPrivacyCheckboxes) {
        await this.registerNewUser()
        return
      }

      // Otherwise, check if user exists
      this.sending = true

      try {
        const response = await this.requestLogin({email: this.email})

        // Backend returns: { status: "token_sent"|"not_found", email: "..." }
        if (response.status === 'not_found') {
          // New user - show privacy checkboxes
          this.isNewUser = true
          this.showPrivacyCheckboxes = true
        } else if (response.status === 'token_sent') {
          // Existing user - token sent, go to verification
          this.isNewUser = false
          this.step = 'token-verification'
          //this.startResendCountdown()
        }
      } catch (error) {
        console.error('Failed to request login:', error)
        this.serverErrors = (error.response && error.response.data && error.response.data.non_field_errors) ||
            (error.response && error.response.data && error.response.data.email) ||
            [this.$t('forms.email_token_login.error_send')]
      } finally {
        this.sending = false
      }
    },

    async registerNewUser() {
      this.sending = true

      try {
        // Build payload with privacy checkboxes
        let payload = {
          email: this.email,
          language: this.$i18n.locale || 'it',
          first_name: '',
          last_name: ''
        }

        // Add checkbox consents
        if (this.getEventOwnerId && this.getPrivacyCheckboxes.length) {
          payload = {...payload, ...this.userCheckboxChoices}
        } else {
          payload.consent_privacy_and_terms = this.consent_privacy_and_terms
        }

        const response = await this.registerUser(payload)

        // Backend returns: { status: "registration_token_sent", email: "..." }
        if (response.status === 'registration_token_sent') {
          this.step = 'token-verification'
          this.showPrivacyCheckboxes = false
        //  this.startResendCountdown()
        }
      } catch (error) {
        console.error('Failed to register user:', error)

        // Handle validation errors
        if (error.response && error.response.data) {
          this.validationErrors = error.response.data
          this.serverErrors = error.response.data.non_field_errors ||
              error.response.data.email ||
              [this.$t('forms.email_token_login.error_register')]
        } else {
          this.serverErrors = [this.$t('forms.email_token_login.error_register')]
        }
      } finally {
        this.sending = false
      }
    },

    async verifyToken(token) {
      this.verifyError = null

      try {
        await this.authenticate({
          method: 'email_token',
          credentials: {
            email: this.email,
            token: token
          },
          remember: true
        })

        // Success
        this.$emit('success')
      } catch (error) {
        console.error('Token verification failed:', error)
        this.verifyError = (error.response && error.response.data && error.response.data.non_field_errors && error.response.data.non_field_errors[0]) ||
            (error.response && error.response.data && error.response.data.token && error.response.data.token[0]) ||
            this.$t('forms.email_token_login.error_verify')

        // Reset token input
        if (this.$refs.tokenInput) {
          this.$refs.tokenInput.reset()
        }
      }
    },

    async resendToken() {
      if (!this.canResend) return

      this.startResendCountdown()

      this.resending = true
      this.verifyError = null

      try {
        // For existing users, resend with requestLogin
        // For new users who completed registration, they already have a token
        await this.requestLogin({email: this.email})
        //this.startResendCountdown()
        if (this.$refs.tokenInput) {
          this.$refs.tokenInput.reset()
        }
      } catch (error) {
        console.error('Failed to resend token:', error)
        this.verifyError = this.$t('forms.email_token_login.error_resend')
      } finally {
        this.resending = false
      }
    },

    clearError() {
      this.verifyError = null
    },

    goBackToEmail() {
      this.step = 'email-input'
      this.verifyError = null
      this.serverErrors = []
      this.ageConfirmed = false
      this.ageError = false
      this.consentPrivacy = false
      this.consentMarketing = false
      this.consentError = false
      this.isNewUser = false
      this.email = ''
      this.showPrivacyCheckboxes = false
      if (this.resendTimer) {
        clearInterval(this.resendTimer)
      }
    },

    startResendCountdown() {
      this.resendCountdown = 30

      if (this.resendTimer) {
        clearInterval(this.resendTimer)
      }

      this.resendTimer = setInterval(() => {
        this.resendCountdown--
        if (this.resendCountdown <= 0) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      }, 1000)
    }
  }
}
</script>

